// Custom node components for the VoiceFabric flow canvas.
// Per-rule view: trigger / condition / action.
// Domain-overview view: ruleCluster (compact card per rule).
import React from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  Zap, GitBranch, Webhook, FileText, Link2, Mail, Calendar, Ticket,
  AlertCircle, ChevronRight,
} from 'lucide-react';

const ACTION_ICON = {
  log: FileText,
  webhook: Webhook,
  hubspot: Link2,
  freshdesk: Ticket,
  gohighlevel: Link2,
  ms_calendar: Calendar,
  make: Link2,
  n8n: Link2,
  email: Mail,
};

const baseCard =
  'rounded-xl border bg-[#0E1422] shadow-lg shadow-black/40 px-4 py-3 w-[240px] text-left transition-shadow';

// ── Trigger (rule prompt) ──
export const TriggerNode = ({ data, selected }) => (
  <div
    className={`${baseCard} ${
      selected ? 'border-teal-400/60 shadow-teal-400/10' : 'border-teal-500/30'
    }`}
    data-testid={`flow-trigger-${data.ruleId || ''}`}
  >
    <div className="flex items-center gap-2 mb-1.5">
      <div className="w-6 h-6 rounded-md bg-teal-500/15 flex items-center justify-center">
        <Zap className="w-3.5 h-3.5 text-teal-300" />
      </div>
      <span className="text-[10px] uppercase tracking-wider text-teal-300/80">Trigger</span>
    </div>
    <p className="text-white text-sm font-semibold truncate">{data.label || 'Rule'}</p>
    <p className="text-white/40 text-xs mt-1 line-clamp-2">{data.prompt || ''}</p>
    <Handle type="source" position={Position.Right} className="!bg-teal-400" />
  </div>
);

// ── Condition (when / when_expr) ──
export const ConditionNode = ({ data, selected }) => {
  const expr = (data.when_expr || '').trim();
  const wherePairs = Object.entries(data.when || {});
  const hasGuard = expr || wherePairs.length > 0;
  return (
    <div
      className={`${baseCard} ${
        selected ? 'border-amber-400/60 shadow-amber-400/10' : 'border-amber-500/30'
      }`}
      data-testid={`flow-condition-${data.branchId}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-6 h-6 rounded-md bg-amber-500/15 flex items-center justify-center">
          <GitBranch className="w-3.5 h-3.5 text-amber-300" />
        </div>
        <span className="text-[10px] uppercase tracking-wider text-amber-300/80">
          {hasGuard ? 'Condition' : 'Default'}
        </span>
      </div>
      <p className="text-white text-sm font-medium truncate">{data.label || 'Branch'}</p>
      {expr ? (
        <p className="text-amber-200/70 text-[11px] mt-1 font-mono truncate">{expr}</p>
      ) : wherePairs.length > 0 ? (
        <p className="text-amber-200/60 text-[11px] mt-1 font-mono truncate">
          {wherePairs.map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(' & ')}
        </p>
      ) : (
        <p className="text-white/30 text-[11px] mt-1 italic">always matches (fallback)</p>
      )}
      <Handle type="target" position={Position.Left} className="!bg-amber-400" />
      <Handle type="source" position={Position.Right} className="!bg-amber-400" />
    </div>
  );
};

// ── Action (dispatched when branch matches) ──
export const ActionNode = ({ data, selected }) => {
  const Icon = ACTION_ICON[data.action_type] || FileText;
  return (
    <div
      className={`${baseCard} ${
        selected ? 'border-violet-400/60 shadow-violet-400/10' : 'border-violet-500/30'
      }`}
      data-testid={`flow-action-${data.branchId}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-6 h-6 rounded-md bg-violet-500/15 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-violet-300" />
        </div>
        <span className="text-[10px] uppercase tracking-wider text-violet-300/80">Action</span>
      </div>
      <p className="text-white text-sm font-medium truncate">
        {data.action_label || data.action_type}
      </p>
      <p className="text-white/40 text-[11px] mt-1 truncate">
        {data.connection_name || (data.action_type === 'log' ? 'log only' : '— no connection —')}
      </p>
      <Handle type="target" position={Position.Left} className="!bg-violet-400" />
      <Handle type="source" position={Position.Right} className="!bg-violet-400" />
    </div>
  );
};

// ── Rule cluster (domain-overview canvas) ──
// Compact card representing a whole rule. Shows name + branch count + the
// distinct action types in use. Click → drill into per-rule canvas.
export const RuleClusterNode = ({ data, selected }) => {
  const branchCount = data.branchCount ?? 0;
  const actionPills = (data.actionTypes || []).slice(0, 4);
  const overflow = (data.actionTypes || []).length - actionPills.length;
  return (
    <div
      className={`${baseCard} ${
        selected ? 'border-teal-400/60 shadow-teal-400/10' : 'border-teal-500/30'
      } ${data.disabled ? 'opacity-50' : ''} cursor-pointer hover:border-teal-400/50 transition-colors`}
      data-testid={`flow-rule-${data.ruleId}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-teal-500/15 flex items-center justify-center">
            <Zap className="w-3.5 h-3.5 text-teal-300" />
          </div>
          <span className="text-[10px] uppercase tracking-wider text-teal-300/80">
            {data.disabled ? 'Disabled' : 'Rule'}
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-white/30" />
      </div>
      <p className="text-white text-sm font-semibold truncate">{data.label || 'Rule'}</p>
      <p className="text-white/40 text-xs mt-0.5">
        {branchCount} branch{branchCount === 1 ? '' : 'es'}
      </p>
      {actionPills.length > 0 && (
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          {actionPills.map((at, i) => {
            const Icon = ACTION_ICON[at] || FileText;
            return (
              <span
                key={`${at}-${i}`}
                className="flex items-center gap-1 text-[10px] text-white/60 bg-white/5 border border-white/10 rounded px-1.5 py-0.5"
              >
                <Icon className="w-2.5 h-2.5" />
                {at}
              </span>
            );
          })}
          {overflow > 0 && (
            <span className="text-[10px] text-white/40">+{overflow}</span>
          )}
        </div>
      )}
      <Handle type="target" position={Position.Left} className="!bg-teal-400" />
      <Handle type="source" position={Position.Right} className="!bg-teal-400" />
    </div>
  );
};

export const NODE_TYPES = {
  trigger: TriggerNode,
  condition: ConditionNode,
  action: ActionNode,
  ruleCluster: RuleClusterNode,
};
